import { CircleHelp, Info } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TooltipModal } from '../molecules/TooltipModal';
import { MdHelpers } from '../../helpers/mdHelpers';
import { TooltipModel } from '../../data/types';
import DatabaseTooltipMethods from '../../data/database.tooltips.methods';
import { inspectionDB } from '../../data/database.structure';

interface TooltipProps extends React.LabelHTMLAttributes<HTMLLabelElement> {
  tooltipKey: string;
  useMouseHover?: boolean;
}

export function Tooltip({ tooltipKey, useMouseHover = false }: TooltipProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShowTooltip, setShowTooltip] = useState(false);
  const [tooltip, setTooltip] = useState<TooltipModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const env = useMemo(() => {
    return import.meta.env.VITE_ENV;
  }, []);

  // Create a single instance of DatabaseTooltipMethods
  const tooltipDbMethods = useMemo(
    () => new DatabaseTooltipMethods(inspectionDB),
    [],
  );

  // Optimized tooltip retrieval with error handling
  const retrieveTooltip = useCallback(async () => {
    try {
      setIsLoading(true);
      const tooltipData = await tooltipDbMethods.getTooltip(tooltipKey);
      setTooltip(tooltipData);
    } catch (error) {
      console.error(`Failed to load tooltip: ${tooltipKey}`, error);
      setTooltip({
        key: MdHelpers.normaliseTooltipKeyComponent(tooltipKey),
        heading: '',
        markdownBody: '',
        htmlBody: '',
        isModified: false,
      });
    } finally {
      setIsLoading(false);
    }
  }, [tooltipDbMethods, tooltipKey]);

  // Memoized HTML content processing
  const innerHtml = useMemo(() => {
    if (!tooltip?.htmlBody) return '';
    return MdHelpers.getHtmlElementForHtmlString(
      tooltip.htmlBody,
      MdHelpers.defaultMarkdownStyles,
    );
  }, [tooltip?.htmlBody]);

  // Optimized content check
  const hasContent = useMemo(() => {
    return Boolean(
      tooltip?.htmlBody &&
      tooltip.htmlBody.trim() &&
      !tooltip.htmlBody.includes('TODO'),
    );
  }, [tooltip?.htmlBody]);

  // Load tooltip on mount or key change
  useEffect(() => {
    retrieveTooltip();
  }, [retrieveTooltip]);

  if (
    !isLoading &&
    !hasContent &&
    !['local', 'dv', '', undefined].includes(env)
  ) {
    return <div></div>;
  }

  return (
    <div className="flex items-center justify-center">
      <button
        type="button"
        onClick={() => {
          // Tooltip data is already loaded, just open the modal
          setIsModalOpen(true);
        }}
        onMouseEnter={() => useMouseHover && setShowTooltip(true)}
        onMouseLeave={() => useMouseHover && setShowTooltip(false)}
        className="text-inkDisabled hover:text-inkMuted focus:outline-none"
        disabled={isLoading}
      >
        {isLoading ? (
          <CircleHelp size={20} className="animate-spin" />
        ) : hasContent ? (
          <Info size={20} />
        ) : (
          <CircleHelp size={20} />
        )}
      </button>
      <TooltipModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tooltipKey={tooltipKey}
        content={innerHtml}
      />
    </div>
  );
}
